import { AllowLocalNetworkAccessValue, EmbedCodeProps, EmbedSettings, PlayerConfig } from "./types";
import { LabelledValue, EntryType } from "@kaltura/mediaspace-shared-types";
import { serverUrl, translate } from "@kaltura/mediaspace-shared-utils";

/**
 * get the default embed settings - settings of the player marked as default, or the first one
 */
export const getDefaultEmbedSetting = (embedSettings: EmbedSettings[]) => {
    const defaultPlayers = embedSettings.filter((config) => config.isDefault === true);
    const defaultPlayer = defaultPlayers.length > 0 ? defaultPlayers[0] : embedSettings[0];
    return defaultPlayer;
};

/**
 * get the default player
 */
export const getDefaultPlayer = (embedSettings: EmbedSettings) => {
    return {
        value: embedSettings.playerId + embedSettings.playerDisplayName,
        label: embedSettings.playerDisplayName,
    };
};

/**
 * get the embed settings of the given player
 */
export const getSelectedPlayerSettings = (
    embedSettings: EmbedSettings[],
    playerIdandName: string,
    playerDisplayName: string
): EmbedSettings => {
    const selectedSettings = embedSettings.find(
        (settings: EmbedSettings) =>
            settings.playerId + settings.playerDisplayName === playerIdandName
            && settings.playerDisplayName === playerDisplayName
    );
    return selectedSettings ? selectedSettings : embedSettings[0];
};

/**
 * get players options
 */
export const getPlayersOptions = (embedSettings: EmbedSettings[]) => {
    return embedSettings.map((config) => ({
        value: config.playerId + config.playerDisplayName,
        label: config.playerDisplayName,
    }));
};

/**
 * get option label for autocomplete
 */
export const getOptionLabel = (option: any) => option.label;

/**
 * get embed sizes options
 */
export const getEmbedSizesOptions = (embedSizes: string[]) => {
    return embedSizes.map((size) => ({
        value: size,
        label: size === "100%x100%" ? translate("Responsive size") : size,
    }));
};

/**
 * get the default embed size
 */
export const getDefaultEmbedSize = (embedSettings: EmbedSettings) => {
    const embedSize = embedSettings.selectedEmbedSize ? embedSettings.selectedEmbedSize : embedSettings.embedSizes[0];
    return {
        value: embedSize,
        label: embedSize,
    };
};

/**
 * get player v7 config
 */
export const getPlayerV7Config = (
    embedSettings: EmbedSettings[],
    embedCodeParams: EmbedCodeProps,
    selectedPlayer: LabelledValue,
    mediaType: EntryType,
    playerConfig: PlayerConfig
) => {
    const settings = getSelectedPlayerSettings(embedSettings, selectedPlayer.value, selectedPlayer.label);

    if (!settings.isPlayerV7) {
        return undefined;
    }

    const player = embedSettings.find((settings) => settings.playerDisplayName === selectedPlayer.label);
    const config: any = { ...playerConfig };
    config.provider.uiConfId = player?.playerId;

    if (mediaType === EntryType.playlist || mediaType === EntryType.channelplaylists) {
        // playlist entry
        return {
            ...config,
            playlist_id: player?.entryId,
        };
    }
    else {
        // simple entry
        return {
            ...config,
            entry_id: player?.entryId,
        };
    }
};

export const getAllowLocalNetworkAccessValue = (paramValue: AllowLocalNetworkAccessValue) => {
    switch (paramValue) {
        case AllowLocalNetworkAccessValue.All:
            return "*";

        case AllowLocalNetworkAccessValue.On:
            return serverUrl;

        case AllowLocalNetworkAccessValue.Off:
        case null:
        case undefined:
            return null;

        default:
            console.warn("Unexpected `allow-network-access` value");
    }

    return null;
};
